/**
 * SPT — Shop by Product Type
 * Web Components:
 *   - <product-slider>  — tab switching + slider navigation + drag/swipe
 *   - <product-compare> — multi-product compare modal (2–4 products)
 *
 * Cart: Shopify Ajax cart/add.js + Bundled Section Rendering for live drawer & icon updates.
 */

/* ============================================================
   CART HELPERS
   ============================================================ */
function sptGetCartSectionIds() {
  const ids = new Set();
  const drawerWrapper = document.querySelector('[id^="shopify-section-"][id*="cart-drawer"], #shopify-section-cart-drawer');
  if (drawerWrapper) ids.add(drawerWrapper.id.replace('shopify-section-', ''));
  else ids.add('cart-drawer');
  const iconBubbleWrapper = document.querySelector('[id^="shopify-section-"][id*="cart-icon-bubble"], #shopify-section-cart-icon-bubble');
  if (iconBubbleWrapper) ids.add(iconBubbleWrapper.id.replace('shopify-section-', ''));
  else ids.add('cart-icon-bubble');
  const headerWrapper = document.querySelector('[id^="shopify-section-"][id*="header"], #shopify-section-header');
  if (headerWrapper) ids.add(headerWrapper.id.replace('shopify-section-', ''));
  return [...ids];
}

function sptPatchCartCount(itemCount, sections) {
  const iconBubbleKey = Object.keys(sections || {}).find(k => k.includes('cart-icon-bubble'));
  if (iconBubbleKey && sections[iconBubbleKey]) {
    const iconBubbleWrapper = document.querySelector(`#shopify-section-${iconBubbleKey}`);
    if (iconBubbleWrapper) {
      const tmp = document.createElement('div');
      tmp.innerHTML = sections[iconBubbleKey];
      const newBubble = tmp.querySelector('.cart-count-bubble');
      const oldBubble = iconBubbleWrapper.querySelector('.cart-count-bubble');
      if (newBubble && oldBubble) { oldBubble.replaceWith(newBubble); return; }
    }
  }
  const headerKey = Object.keys(sections || {}).find(k => k.includes('header'));
  if (headerKey && sections[headerKey]) {
    const headerWrapper = document.querySelector(`#shopify-section-${headerKey}`);
    if (headerWrapper) {
      const tmp = document.createElement('div');
      tmp.innerHTML = sections[headerKey];
      const newBubble = tmp.querySelector('.cart-count-bubble');
      const oldBubble = headerWrapper.querySelector('.cart-count-bubble');
      if (newBubble && oldBubble) { oldBubble.replaceWith(newBubble); return; }
    }
  }
  document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
    const spans = bubble.querySelectorAll('span');
    if (spans[0]) spans[0].textContent = itemCount;
    if (spans[1]) spans[1].textContent = `${itemCount} items`;
    bubble.classList.toggle('hidden', itemCount === 0);
  });
}

function sptPatchAndOpenCartDrawer(sections, sourceButton) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;
  const drawerKey = Object.keys(sections || {}).find(k => k.includes('cart-drawer') || k === 'cart-drawer');
  if (drawerKey && sections[drawerKey]) {
    const tmp = document.createElement('div');
    tmp.innerHTML = sections[drawerKey];
    const newDrawerInner = tmp.querySelector('.drawer__inner');
    const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');
    if (newDrawerInner && currentDrawerInner) {
      currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      currentDrawerInner.classList.remove('is-empty');
      cartDrawer.classList.remove('is-empty');
    }
  }
  if (typeof cartDrawer.open === 'function') cartDrawer.open(sourceButton);
  else {
    cartDrawer.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    const overlay = document.querySelector('cart-drawer-overlay, .overlay');
    if (overlay) overlay.classList.add('active');
  }
}

async function sptAddToCart(variantId, buttonEl) {
  if (!variantId || buttonEl.disabled) return;
  const textEl = buttonEl.querySelector('.spt__btn-text');
  const originalTxt = textEl?.textContent.trim() || 'Add to Cart';
  const cartRoot = window.Shopify?.routes?.root || '/';
  const cartAddUrl = window.theme?.routes?.cartAdd || `${cartRoot}cart/add.js`;
  buttonEl.classList.add('spt__btn--loading');
  buttonEl.disabled = true;
  buttonEl.setAttribute('aria-busy', 'true');
  try {
    const addRes = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    });
    if (!addRes.ok) { const errData = await addRes.json().catch(() => ({})); throw new Error(errData.description || 'Could not add item to cart.'); }
    const addData = await addRes.json();
    if (window.theme?.settings?.cartType === 'page') {
      window.location.assign(window.theme?.routes?.cartPage || `${cartRoot}cart`);
    } else {
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: {
          product: addData,
          addToCartBtn: buttonEl
        }
      }));
    }
    if (textEl) { textEl.textContent = '✓ Added!'; setTimeout(() => { textEl.textContent = originalTxt; }, 2200); }
  } catch (err) {
    console.error('[SPT] Add to cart error:', err);
    if (textEl) { textEl.textContent = 'Error — try again'; setTimeout(() => { textEl.textContent = originalTxt; }, 2500); }
  } finally {
    buttonEl.classList.remove('spt__btn--loading');
    buttonEl.disabled = false;
    buttonEl.removeAttribute('aria-busy');
  }
}

/* ============================================================
   <product-slider> Web Component
   ============================================================ */
class ProductSlider extends HTMLElement {
  constructor() { super(); this._isDragging = false; this._startX = 0; this._startScrollLeft = 0; this._mouseMoved = 0; }
  connectedCallback() {
    this._track = this.querySelector('.spt__track');
    this._wrapper = this.querySelector('.spt__track-wrapper');
    this._prevBtn = this.querySelector('.spt__arrow--prev');
    this._nextBtn = this.querySelector('.spt__arrow--next');
    this._cards = Array.from(this._track?.querySelectorAll('.spt__card') || []);
    if (!this._track || this._cards.length === 0) return;
    this._bindArrows(); this._bindDrag(); this._bindAddToCart(); this._updateArrows();
    this._wrapper.addEventListener('scroll', () => this._updateArrows());
  }
  _bindArrows() {
    this._prevBtn?.addEventListener('click', () => this._slide(-1));
    this._nextBtn?.addEventListener('click', () => this._slide(1));
  }
  _slide(direction) {
    const cardWidth = this._cardWidth();
    this._wrapper.scrollTo({ left: this._wrapper.scrollLeft + (direction * cardWidth), behavior: 'smooth' });
  }
  _visibleCount() {
    if (!this._wrapper) return 4;
    const w = this._wrapper.offsetWidth;
    if (w <= 599) return 1; if (w <= 899) return 2; if (w <= 1199) return 3; return 4;
  }
  _cardWidth() {
    const card = this._cards[0]; if (!card) return 0;
    return card.offsetWidth + (parseInt(getComputedStyle(this._track).gap) || 20);
  }
  _updateArrows() {
    const scrollLeft = this._wrapper.scrollLeft;
    const maxScroll = this._wrapper.scrollWidth - this._wrapper.clientWidth;
    if (this._prevBtn) this._prevBtn.disabled = scrollLeft <= 1;
    if (this._nextBtn) this._nextBtn.disabled = scrollLeft >= maxScroll - 1;
  }
  _bindDrag() {
    const w = this._wrapper; if (!w) return;
    w.addEventListener('mousedown', e => this._onDragStart(e));
    w.addEventListener('mousemove', e => this._onDragMove(e));
    w.addEventListener('mouseup', e => this._onDragEnd(e));
    w.addEventListener('mouseleave', e => this._onDragEnd(e));
    w.addEventListener('touchstart', e => this._onDragStart(e), { passive: true });
    w.addEventListener('touchmove', e => this._onDragMove(e), { passive: true });
    w.addEventListener('touchend', e => this._onDragEnd(e));
  }
  _getX(e) { return e.touches ? e.touches[0].clientX : e.clientX; }
  _onDragStart(e) {
    this._isDragging = true; this._startX = this._getX(e);
    this._startScrollLeft = this._wrapper.scrollLeft; this._mouseMoved = 0;
    this._wrapper.style.scrollBehavior = 'auto';
  }
  _onDragMove(e) {
    if (!this._isDragging) return;
    this._mouseMoved = this._startX - this._getX(e);
    this._wrapper.scrollLeft = this._startScrollLeft + this._mouseMoved;
  }
  _onDragEnd() {
    if (!this._isDragging) return;
    this._isDragging = false; this._wrapper.style.scrollBehavior = 'smooth';
    this._updateArrows(); this._mouseMoved = 0;
  }
  _bindAddToCart() {
    this.addEventListener('click', e => {
      const btn = e.target.closest('.spt__btn--cart'); if (!btn) return;
      sptAddToCart(btn.dataset.variantId, btn);
    });
  }
}
customElements.define('product-slider', ProductSlider);

/* ============================================================
   MULTI-PRODUCT COMPARE — Global State + Floating Bar
   ============================================================ */
const sptCompareList = [];
const SPT_MAX_COMPARE = 4;

function sptGetProductFromBtn(btn) {
  return {
    id: btn.dataset.productId, handle: btn.dataset.productHandle,
    title: btn.dataset.productTitle, price: btn.dataset.productPrice,
    image: btn.dataset.productImage, url: btn.dataset.productUrl,
    available: btn.dataset.productAvailable === 'true',
    variantId: btn.dataset.variantId, rating: btn.dataset.rating,
  };
}

function sptToggleCompare(product) {
  const idx = sptCompareList.findIndex(p => p.id === product.id);
  if (idx > -1) { sptCompareList.splice(idx, 1); }
  else {
    if (sptCompareList.length >= SPT_MAX_COMPARE) return false;
    sptCompareList.push(product);
  }
  sptSyncCardStates();
  sptUpdateBar();
  return true;
}

function sptSyncCardStates() {
  document.querySelectorAll('.spt__btn--compare').forEach(btn => {
    const inList = sptCompareList.some(p => p.id === btn.dataset.productId);
    btn.classList.toggle('spt__btn--compare-active', inList);
    const label = btn.querySelector('.spt__compare-label');
    if (label) label.textContent = inList ? 'Added' : 'Compare';
  });
}

function sptUpdateBar() {
  const bar = document.getElementById('spt-compare-bar');
  const productsEl = document.getElementById('spt-compare-bar-products');
  const countEl = document.getElementById('spt-compare-bar-count');
  const goBtn = document.getElementById('spt-compare-bar-go');
  if (!bar) return;
  if (sptCompareList.length === 0) { bar.setAttribute('hidden', ''); return; }
  bar.removeAttribute('hidden');
  countEl.textContent = `${sptCompareList.length} product${sptCompareList.length > 1 ? 's' : ''} selected`;
  goBtn.disabled = sptCompareList.length < 2;
  productsEl.innerHTML = sptCompareList.map(p => `
    <div class="spt-compare-bar__product" data-id="${p.id}">
      <img src="${p.image}" alt="${sptEsc(p.title)}" class="spt-compare-bar__thumb">
      <button class="spt-compare-bar__remove" data-id="${p.id}" aria-label="Remove ${sptEsc(p.title)}">×</button>
    </div>
  `).join('');
}

function sptEsc(str) { const d = document.createElement('div'); d.textContent = str || ''; return d.innerHTML; }

/* Bar event listeners */
document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', e => {
    // Toggle compare from card buttons
    const cmpBtn = e.target.closest('.spt__btn--compare');
    if (cmpBtn) {
      const product = sptGetProductFromBtn(cmpBtn);
      const ok = sptToggleCompare(product);
      if (!ok) { cmpBtn.classList.add('spt__btn--compare-shake'); setTimeout(() => cmpBtn.classList.remove('spt__btn--compare-shake'), 500); }
      return;
    }
    // Remove from bar
    const removeBtn = e.target.closest('.spt-compare-bar__remove');
    if (removeBtn) {
      const idx = sptCompareList.findIndex(p => p.id === removeBtn.dataset.id);
      if (idx > -1) sptCompareList.splice(idx, 1);
      sptSyncCardStates(); sptUpdateBar(); return;
    }
    // Clear all
    if (e.target.closest('#spt-compare-bar-clear')) {
      sptCompareList.length = 0; sptSyncCardStates(); sptUpdateBar(); return;
    }
    // Compare Now
    if (e.target.closest('#spt-compare-bar-go')) {
      const cmp = document.querySelector('product-compare');
      if (cmp) cmp.openWithProducts([...sptCompareList]);
    }
  });
});

/* ============================================================
   <product-compare> Web Component — Multi-product
   ============================================================ */
class ProductCompare extends HTMLElement {
  constructor() { super(); this._products = []; this._searchTimeout = null; }

  connectedCallback() {
    this._closeBtn = this.querySelector('.spt-compare__close');
    this._backdrop = this.querySelector('.spt-compare__backdrop');
    this._searchInput = this.querySelector('.spt-compare__search-input');
    this._resultsList = this.querySelector('.spt-compare__results');
    this._searchPanel = this.querySelector('.spt-compare__search-panel');
    this._tableWrapper = this.querySelector('#spt-compare-table-wrapper');
    this._table = this.querySelector('#spt-compare-table');
    this._addMoreBtn = this.querySelector('#spt-compare-add-more');

    this._closeBtn?.addEventListener('click', () => this.close());
    this._backdrop?.addEventListener('click', () => this.close());
    this._searchInput?.addEventListener('input', e => this._onSearchInput(e));
    this._addMoreBtn?.addEventListener('click', () => {
      if (this._products.length >= SPT_MAX_COMPARE) return;
      this._toggleSearchPanel(true);
      setTimeout(() => this._searchInput?.focus(), 100);
    });

    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !this.hidden) this.close(); });

    this.addEventListener('click', e => {
      const cartBtn = e.target.closest('.spt-compare__action-btn--cart');
      if (cartBtn && !cartBtn.disabled) {
        const variantId = cartBtn.dataset.variantId;
        this.close();
        sptAddToCart(variantId, cartBtn);
        return;
      }
      const removeBtn = e.target.closest('.spt-compare__action-btn--remove');
      if (removeBtn) {
        const id = removeBtn.dataset.productId;
        this._products = this._products.filter(p => p.id !== id);
        // Also remove from global list
        const gIdx = sptCompareList.findIndex(p => p.id === id);
        if (gIdx > -1) sptCompareList.splice(gIdx, 1);
        sptSyncCardStates(); sptUpdateBar();
        if (this._products.length < 2) { this._toggleSearchPanel(true); }
        this._renderTable();
      }
    });
  }

  openWithProducts(products) {
    this._products = products;
    this.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    this._clearSearch();
    this._toggleSearchPanel(false);
    this._updateAddMoreBtn();
    this._renderTable();
  }

  close() {
    this.setAttribute('hidden', '');
    document.body.style.overflow = '';
    this._clearSearch();
  }

  _updateAddMoreBtn() {
    if (this._addMoreBtn) {
      this._addMoreBtn.disabled = this._products.length >= SPT_MAX_COMPARE;
      this._addMoreBtn.style.display = this._products.length >= SPT_MAX_COMPARE ? 'none' : '';
    }
  }

  _toggleSearchPanel(show) {
    if (show) this._searchPanel?.removeAttribute('hidden');
    else this._searchPanel?.setAttribute('hidden', '');
  }

  _clearSearch() {
    if (this._searchInput) this._searchInput.value = '';
    if (this._resultsList) { this._resultsList.innerHTML = ''; this._resultsList.setAttribute('hidden', ''); }
  }

  _onSearchInput(e) {
    clearTimeout(this._searchTimeout);
    const q = e.target.value.trim();
    if (q.length < 2) { this._resultsList.setAttribute('hidden', ''); this._resultsList.innerHTML = ''; return; }
    this._searchTimeout = setTimeout(() => this._doSearch(q), 320);
  }

  async _doSearch(query) {
    this._resultsList.removeAttribute('hidden');
    this._resultsList.innerHTML = `<li class="spt-compare__loading">Searching…</li>`;
    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`);
      const data = await res.json();
      const products = (data?.resources?.results?.products || []).filter(
        p => !this._products.some(ep => String(ep.id) === String(p.id))
      );
      this._renderResults(products);
    } catch { this._resultsList.innerHTML = `<li class="spt-compare__result-no">Search unavailable. Try again.</li>`; }
  }

  _renderResults(products) {
    if (!products.length) { this._resultsList.innerHTML = `<li class="spt-compare__result-no">No products found.</li>`; return; }
    this._resultsList.innerHTML = products.map(p => {
      const img = p.image || '';
      const price = p.price ? this._formatMoney(p.price) : '';
      return `<li class="spt-compare__result-item" role="option" tabindex="0"
        data-handle="${p.handle}" data-title="${this._esc(p.title)}"
        data-price="${this._esc(price)}" data-image="${this._esc(img)}"
        data-url="${this._esc(p.url)}" data-id="${p.id || ''}">
        ${img ? `<img class="spt-compare__result-img" src="${img}" alt="${this._esc(p.title)}" loading="lazy">` : ''}
        <div class="spt-compare__result-info">
          <div class="spt-compare__result-name">${this._esc(p.title)}</div>
          ${price ? `<div class="spt-compare__result-price">${price}</div>` : ''}
        </div></li>`;
    }).join('');
    this._resultsList.querySelectorAll('.spt-compare__result-item').forEach(item => {
      const select = () => this._selectProduct(item);
      item.addEventListener('click', select);
      item.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') select(); });
    });
  }

  async _selectProduct(item) {
    const handle = item.dataset.handle;
    let variantId = '', available = false;
    try {
      const res = await fetch(`/products/${handle}.js`);
      const data = await res.json();
      const v = data.variants?.[0];
      variantId = v?.id || ''; available = v?.available || false;
    } catch { /* silent */ }

    const newProduct = {
      id: item.dataset.id, handle, title: item.dataset.title,
      price: item.dataset.price, image: item.dataset.image,
      url: item.dataset.url, available, variantId: String(variantId), rating: '5.0',
    };

    this._products.push(newProduct);
    // Also add to global list if not present
    if (!sptCompareList.some(p => p.id === newProduct.id)) {
      sptCompareList.push(newProduct);
      sptSyncCardStates(); sptUpdateBar();
    }

    this._clearSearch();
    this._toggleSearchPanel(false);
    this._updateAddMoreBtn();
    this._renderTable();
  }

  _renderTable() {
    if (!this._table) return;
    const ps = this._products;
    if (ps.length < 2) {
      this._tableWrapper?.setAttribute('hidden', '');
      this._table.innerHTML = '';
      this._updateAddMoreBtn();
      return;
    }
    this._tableWrapper?.removeAttribute('hidden');

    const colW = ps.length === 2 ? '40%' : ps.length === 3 ? '28%' : '22%';
    const labelW = ps.length === 2 ? '20%' : ps.length === 3 ? '16%' : '12%';

    const makeRow = (label, cells, altClass = '') => {
      return `<tr class="spt-compare__table-row ${altClass}">
        <th class="spt-compare__table-label" scope="row">${label}</th>
        ${cells.map(c => `<td class="spt-compare__table-cell">${c}</td>`).join('')}
      </tr>`;
    };

    let html = `<colgroup><col style="width:${labelW}">`;
    ps.forEach(() => { html += `<col style="width:${colW}">`; });
    html += `</colgroup>`;

    // Image row
    html += `<thead>${makeRow('Image', ps.map(p => this._imgHTML(p.image, p.title)))}</thead><tbody>`;
    // Product name
    html += makeRow('Product', ps.map(p => `<a href="${p.url}" style="color:var(--spt-navy);font-weight:500;text-decoration:none">${this._esc(p.title)}</a>`));
    // Price
    html += makeRow('Price', ps.map(p => `<strong>${this._esc(p.price)}</strong>`), 'spt-compare__table-row--alt');
    // Availability
    html += makeRow('Availability', ps.map(p => this._availBadge(p.available)));
    // Rating
    html += makeRow('Rating', ps.map(p => this._starsHTML(p.rating)), 'spt-compare__table-row--alt');
    // Actions
    html += makeRow('Actions', ps.map(p => this._actionsHTML(p)));
    html += '</tbody>';

    this._table.innerHTML = html;
    this._updateAddMoreBtn();
  }

  _imgHTML(src, alt) {
    if (!src) return '<div style="width:100%;aspect-ratio:1/1;background:#f0f2f5;border-radius:8px;"></div>';
    return `<img class="spt-compare__prod-img" src="${src}" alt="${this._esc(alt)}" loading="lazy">`;
  }
  _availBadge(available) {
    return available
      ? `<span class="spt-compare__badge spt-compare__badge--in">✓ In Stock</span>`
      : `<span class="spt-compare__badge spt-compare__badge--out">✕ Out of Stock</span>`;
  }
  _starsHTML(rating) {
    const r = Math.max(0, Math.min(5, parseFloat(rating) || 5));
    const percent = (r / 5) * 100;
    return `<span class="spt-compare__stars" style="--spt-rating-percent:${percent}%">
      <span class="spt-compare__stars-base">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
      <span class="spt-compare__stars-fill">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
    </span> <span style="font-size:13px;font-weight:500">${r.toFixed(1)}</span>`;
  }
  _actionsHTML(product) {
    const cartBtn = product.available
      ? `<button class="spt-compare__action-btn spt-compare__action-btn--cart" data-variant-id="${product.variantId}"><span class="spt__btn-text">Add to Cart</span></button>`
      : `<button class="spt-compare__action-btn spt-compare__action-btn--cart" disabled style="opacity:0.45;cursor:not-allowed"><span class="spt__btn-text">Out of Stock</span></button>`;
    const removeBtn = this._products.length > 2
      ? `<button class="spt-compare__action-btn spt-compare__action-btn--remove" data-product-id="${product.id}">Remove</button>`
      : '';
    return cartBtn + removeBtn;
  }
  _esc(str) { const d = document.createElement('div'); d.textContent = str || ''; return d.innerHTML; }
  _formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    return `${window.Shopify?.currency?.symbol || '£'}${(cents / 100).toFixed(2)}`;
  }
}
customElements.define('product-compare', ProductCompare);

/* ============================================================
   Tab Switching
   ============================================================ */
document.querySelectorAll('.spt__tabs').forEach(tabsEl => {
  tabsEl.addEventListener('click', e => {
    const tab = e.target.closest('.spt__tab'); if (!tab) return;
    const section = tabsEl.closest('.spt'); if (!section) return;
    section.querySelectorAll('.spt__tab').forEach(t => { t.classList.remove('spt__tab--active'); t.setAttribute('aria-selected', 'false'); });
    section.querySelectorAll('.spt__panel').forEach(p => { p.classList.remove('spt__panel--active'); p.setAttribute('hidden', ''); });
    tab.classList.add('spt__tab--active');
    tab.setAttribute('aria-selected', 'true');
    const panelId = tab.getAttribute('aria-controls');
    const panel = section.querySelector(`#${panelId}`);
    if (panel) {
      panel.classList.add('spt__panel--active');
      panel.removeAttribute('hidden');
      const slider = panel.querySelector('product-slider');
      if (slider?._wrapper) { slider._wrapper.scrollLeft = 0; slider._updateArrows(); }
    }
  });
});